// =============================================================================
// FLUID ALCHEMIST ANIMATION SYSTEM
// Kinetic typography, magnetic interactions, spring physics
// =============================================================================

// =============================================================================
// UNIFIED SPRING SCALE (Swiss-validated via Motion MCP)
// Consistent spring physics across all components
// =============================================================================

export const springs = {
  // Snappy: Buttons, toggles, micro-interactions (fast, precise)
  snappy: {
    type: "spring" as const,
    stiffness: 400,
    damping: 30
  },
  // Default: Cards, reveals, general animations (balanced)
  default: {
    type: "spring" as const,
    stiffness: 300,
    damping: 30,
    mass: 1
  },
  // Gentle: Page transitions, large elements (smooth, elegant)
  gentle: {
    type: "spring" as const,
    stiffness: 200,
    damping: 25
  }
} as const;

// Golden Spring - signature spring for Fluid Alchemist
// Swift but solid feel with controlled overshoot
export const goldenSpring = springs.default;

// Swift ease for curtain reveals - cubic-bezier [0.16, 1, 0.3, 1]
export const swiftEase = [0.16, 1, 0.3, 1] as const;

// Magnetic spring for dock hover - snappier response
export const magneticSpring = {
  type: "spring" as const,
  stiffness: 300,
  damping: 20
};

// Tilt spring for 3D card interactions (uses snappy)
export const tiltSpring = springs.snappy;

// =============================================================================
// PAGE STACK TRANSITIONS
// Cards-stacking effect for view changes
// =============================================================================

export const pageStackVariants = {
  initial: {
    opacity: 0,
    y: 50,
    scale: 0.98
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: goldenSpring
  },
  exit: {
    opacity: 0,
    scale: 0.98,
    filter: "blur(2px)", // Reduced from 5px for subtler effect
    transition: springs.default // Symmetric spring exit (was duration tween)
  }
};

// =============================================================================
// CURTAIN REVEAL - KINETIC TYPOGRAPHY
// Words slide up from behind overflow mask
// =============================================================================

export const curtainRevealContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.2
    }
  }
};

export const curtainRevealWord = {
  hidden: {
    y: "100%",
    opacity: 0
  },
  visible: {
    y: 0,
    opacity: 1,
    transition: goldenSpring
  }
};

// Line-by-line variant for multi-line headlines
export const curtainRevealLine = {
  hidden: {
    y: "110%",
    opacity: 0,
    rotateX: -10
  },
  visible: {
    y: 0,
    opacity: 1,
    rotateX: 0,
    transition: {
      ...goldenSpring,
      opacity: { duration: 0.4 }
    }
  }
};

// =============================================================================
// MAGNETIC DOCK ANIMATIONS
// Gaussian distribution scaling on hover
// =============================================================================

export const dockItemVariants = {
  initial: { scale: 1 },
  hover: {
    scale: 1.4,
    transition: magneticSpring
  },
  tap: {
    scale: 0.95,
    transition: { duration: 0.1 }
  }
};

export const dockLampVariants = {
  initial: { opacity: 0, scale: 0 },
  active: {
    opacity: 1,
    scale: 1,
    transition: magneticSpring
  }
};

// =============================================================================
// TILT CARD ANIMATIONS
// 3D perspective tilt with shine overlay
// =============================================================================

export const tiltCardVariants = {
  initial: {
    rotateX: 0,
    rotateY: 0,
    scale: 1
  },
  hover: {
    scale: 1.02,
    transition: tiltSpring
  }
};

// =============================================================================
// MAGNETIC BUTTON ANIMATIONS
// Subtle attraction to cursor
// =============================================================================

export const magneticButtonVariants = {
  initial: { x: 0, y: 0 },
  hover: {
    transition: magneticSpring
  }
};

// =============================================================================
// SCROLL PROGRESS
// Smooth scroll-linked animation
// =============================================================================

export const scrollProgressSpring = {
  stiffness: 100,
  damping: 30,
  restDelta: 0.001
};

// =============================================================================
// MONUMENTAL FOOTER
// Large-scale reveal animations
// =============================================================================

export const monumentalReveal = {
  hidden: {
    opacity: 0,
    scale: 0.9,
    y: 40
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      ...goldenSpring,
      duration: 0.8
    }
  }
};

// =============================================================================
// UTILITY ANIMATIONS
// Common patterns for micro-interactions
// =============================================================================

// Fade in with blur (premium reveal)
export const fadeInBlur = {
  hidden: { opacity: 0, filter: "blur(10px)" },
  visible: {
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.6 }
  }
};

// Scale from center
export const scaleIn = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: goldenSpring
  }
};

// Slide in from direction
export const slideIn = (direction: 'left' | 'right' | 'up' | 'down', distance = 30) => ({
  hidden: {
    opacity: 0,
    x: direction === 'left' ? -distance : direction === 'right' ? distance : 0,
    y: direction === 'up' ? distance : direction === 'down' ? -distance : 0
  },
  visible: {
    opacity: 1,
    x: 0,
    y: 0,
    transition: goldenSpring
  }
});

// Stagger children - default config
export const fluidStagger = {
  staggerChildren: 0.06,
  delayChildren: 0.1
};

// Container with stagger
export const fluidStaggerContainer = {
  hidden: {},
  visible: {
    transition: fluidStagger
  }
};
